/**
 * Integrytas Group - FieldRenderer infrastructure.
 *
 * Bridges a module's source-of-truth FieldDef[] descriptor with the ULD layout
 * engine: a screen declares its fields once, passes them through useUldLayout /
 * useUldSectionedLayout, and hands the merged layout here to render
 * order/visibility-aware slots.
 *
 * Two modes:
 *  - FLAT (no `sections` prop): one grid; design editor reorders/hides fields.
 *  - SECTIONED (`sections` prop): fields grouped into bordered section cards; the
 *    design editor (dnd-kit multi-container) reorders within a section, moves a
 *    field across sections, and reorders whole sections.
 *
 * When designMode is true (and pageCode + session are provided), the design editor
 * panel is shown above the form and saves the delta to uld_layout_delta.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Eye, EyeOff, Lock, LockOpen, Asterisk } from 'lucide-react';
import { asfWorkspaceCode } from '../../lib/asf';
import { setDesignDirty } from '../../lib/design_dirty';
import { usePersonalizationOptional, usePersonalization } from '../../lib/use_personalization';
import type { AuthSession } from '../../types/auth';
import { DndContext, closestCenter, closestCorners } from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { applyLayoutDelta, applySectionedLayout } from '../../lib/uld_merge';
import type { UldDeltaAction, UldLayoutItem, UldSection, SectionDef } from '../../lib/uld_merge';
import { saveUldDesign, tierErrorMessage } from '../../hooks/use_uld_layout';

export interface FieldDef {
  key: string;
  type: 'text' | 'number' | 'textarea' | 'boolean' | 'select' | 'multiselect' | 'custom';
  label: string;
  required?: boolean;
  col_span?: number;
  /** Section this field belongs to by default (section mode only). */
  section?: string;
}

export type { SectionDef };

export type FieldLayoutItem = FieldDef & { col_span: number; label: string };

export function toUldCoreLayout(fields: FieldDef[]): UldLayoutItem[] {
  return fields.map(f => ({ key: f.key, label: f.label, col_span: f.col_span ?? 1, visible: true, section: f.section ?? null }));
}

const GRID_COLS_MAP: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const COL_SPAN_MAP: Record<number, string> = {
  1: '',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  12: 'col-span-full',
};

// ---------------------------------------------------------------------------
// SortableFieldCard — one drag-and-drop card in the design editor
// ---------------------------------------------------------------------------

interface SortableFieldCardProps {
  id: string;
  label: string;
  type: string;
  hidden: boolean;
  required: boolean;
  readonly: boolean;
  onToggleHide: () => void;
  onToggleRequired: () => void;
  onToggleReadonly: () => void;
}

// A small square toggle button used for the per-field behavior switches.
function CardToggle({ active, on, off, title, onClick, activeClass = 'border-primary bg-primary text-white' }: {
  active: boolean; on: React.ReactNode; off: React.ReactNode; title: string; onClick: () => void; activeClass?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={'flex h-6 w-6 items-center justify-center rounded border ' +
        (active ? activeClass : 'border-csm-border bg-white text-csm-muted hover:text-primary')}
    >
      {active ? on : off}
    </button>
  );
}

function SortableFieldCard({
  id, label, type, hidden, required, readonly,
  onToggleHide, onToggleRequired, onToggleReadonly,
}: SortableFieldCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-1 flex items-center gap-2 rounded border px-2 py-1.5 ${
        hidden
          ? 'border-csm-border bg-csm-surface-alt opacity-60'
          : 'border-csm-border bg-white'
      }`}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab select-none text-csm-muted hover:text-csm-ink active:cursor-grabbing"
        title={t('common.field_editor.drag_reorder', 'Drag to reorder')}
      >
        <GripVertical size={14} />
      </span>

      {/* Field label */}
      <span
        className={`flex-1 text-csm-sm ${hidden ? 'line-through text-csm-ink-soft' : 'text-csm-ink'}`}
      >
        {label}
      </span>

      {/* Type badge */}
      <span className="text-csm-2xs text-csm-muted">{type}</span>

      {/* Show / hide */}
      <CardToggle
        active={!hidden}
        on={<Eye size={13} />}
        off={<EyeOff size={13} />}
        title={hidden ? t('common.field_editor.show_field', 'Show field') : t('common.field_editor.hide_field', 'Hide field')}
        onClick={onToggleHide}
        activeClass="border-csm-border bg-white text-csm-ink-soft hover:text-csm-danger"
      />
      {/* Read-only */}
      <CardToggle
        active={readonly}
        on={<Lock size={13} />}
        off={<LockOpen size={13} />}
        title={readonly ? t('common.field_editor.allow_editing', 'Allow editing') : t('common.field_editor.make_readonly', 'Make read-only')}
        onClick={onToggleReadonly}
      />
      {/* Required */}
      <CardToggle
        active={required}
        on={<Asterisk size={13} />}
        off={<Asterisk size={13} />}
        title={required ? t('common.field_editor.make_optional', 'Make optional') : t('common.field_editor.make_required', 'Make required')}
        onClick={onToggleRequired}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared: seed per-field behavior sets from a merged layout, and diff the
// editor's current behavior into set_visible / set_required / set_readonly
// deltas (only emitting changes, so the edit tier overrides lower tiers both
// ways). Order/section deltas are computed separately by each panel.
// ---------------------------------------------------------------------------

interface BehaviorSets { hidden: Set<string>; required: Set<string>; readonly: Set<string> }

function seedBehavior(items: { key: string; visible?: boolean; required?: boolean; readonly?: boolean }[]): BehaviorSets {
  const hidden = new Set<string>(), required = new Set<string>(), readonly = new Set<string>();
  for (const it of items) {
    if (it.visible === false) hidden.add(it.key);
    if (it.required === true) required.add(it.key);
    if (it.readonly === true) readonly.add(it.key);
  }
  return { hidden, required, readonly };
}

function behaviorDeltas(allKeys: string[], seeded: BehaviorSets, cur: BehaviorSets): UldDeltaAction[] {
  const out: UldDeltaAction[] = [];
  for (const k of allKeys) {
    if (cur.hidden.has(k)   !== seeded.hidden.has(k))   out.push({ action: 'set_visible',  component_key: k, visible:  !cur.hidden.has(k) });
    if (cur.required.has(k) !== seeded.required.has(k)) out.push({ action: 'set_required', component_key: k, required: cur.required.has(k) });
    if (cur.readonly.has(k) !== seeded.readonly.has(k)) out.push({ action: 'set_readonly', component_key: k, readonly: cur.readonly.has(k) });
  }
  return out;
}

// ---------------------------------------------------------------------------
// DesignEditorPanel — FLAT (single-section) editor, rendered when designMode=true
// ---------------------------------------------------------------------------

interface DesignEditorPanelProps {
  fields: FieldDef[];
  pageCode: string;
  onApplied?: () => void;
}

function DesignEditorPanel({ fields, pageCode, onApplied }: DesignEditorPanelProps) {
  const { t } = useTranslation();
  const ws = asfWorkspaceCode() || '';
  const { editTier, roleOwner, componentDelta, resetComponent } = usePersonalization();
  // Editing baseline = the tiers BELOW the edit tier + this tier's own delta, so
  // the editor reflects what this tier inherits and re-seeds on tier/reset.
  const baseLayout = useMemo(() => applyLayoutDelta(toUldCoreLayout(fields), componentDelta('')).layout, [fields, componentDelta]);
  const [editorOrder, setEditorOrder] = useState<string[]>(() => baseLayout.map(it => it.key));
  const seeded = useMemo(() => seedBehavior(baseLayout), [baseLayout]);
  const [hiddenKeys, setHiddenKeys]     = useState<Set<string>>(() => new Set(seeded.hidden));
  const [requiredKeys, setRequiredKeys] = useState<Set<string>>(() => new Set(seeded.required));
  const [readonlyKeys, setReadonlyKeys] = useState<Set<string>>(() => new Set(seeded.readonly));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fieldMap = useMemo(() => {
    const m = new Map<string, FieldDef>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  // Clear the shared dirty flag when the editor unmounts (design mode exits).
  useEffect(() => () => setDesignDirty(false), []);

  const resetToLayout = useCallback(() => {
    setEditorOrder(baseLayout.map(it => it.key));
    setHiddenKeys(new Set(seeded.hidden));
    setRequiredKeys(new Set(seeded.required));
    setReadonlyKeys(new Set(seeded.readonly));
    setError(null);
    setDesignDirty(false);
  }, [baseLayout, seeded]);

  // Reset = delete this tier's saved personalization for the form; the provider
  // bumps resetTick so the (keyed) panel re-seeds from what remains below.
  const handleReset = useCallback(async () => {
    setBusy(true); setError(null);
    try { await resetComponent(''); setDesignDirty(false); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }, [resetComponent]);

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (key: string) => {
    setDesignDirty(true);
    setter(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDesignDirty(true);
    setEditorOrder(prev => {
      const oldIdx = prev.indexOf(String(active.id));
      const newIdx = prev.indexOf(String(over.id));
      if (oldIdx === -1 || newIdx === -1) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, []);

  const toggleHide     = toggleIn(setHiddenKeys);
  const toggleRequired = toggleIn(setRequiredKeys);
  const toggleReadonly = toggleIn(setReadonlyKeys);

  const handleApply = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const originalOrder = fields.map(f => f.key);
      const deltas: UldDeltaAction[] = [];
      editorOrder.forEach((key, newIdx) => {
        if (originalOrder.indexOf(key) !== newIdx) {
          deltas.push({ action: 'reorder_index', component_key: key, index: newIdx });
        }
      });
      deltas.push(...behaviorDeltas(
        fields.map(f => f.key), seeded,
        { hidden: hiddenKeys, required: requiredKeys, readonly: readonlyKeys },
      ));

      const { error: e } = await saveUldDesign(pageCode, '', editTier ?? 'SYSTEM', ws, deltas);
      if (e) throw new Error(tierErrorMessage(e, roleOwner));
      setDesignDirty(false);
      onApplied?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [editorOrder, hiddenKeys, requiredKeys, readonlyKeys, seeded, fields, pageCode, ws, editTier, roleOwner, onApplied]);

  return (
    <div className="mb-4 rounded-md border border-primary bg-white p-3">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex-1 text-csm-sm font-semibold text-csm-ink">{t('common.field_editor.title', 'Field Layout Editor')}</span>
        <button type="button" onClick={() => void handleApply()} disabled={busy}
          className="rounded border border-primary bg-primary px-3 py-0.5 text-csm-xs text-white hover:opacity-90 disabled:opacity-60">
          {busy ? t('common.field_editor.saving', 'Saving...') : t('common.field_editor.apply', 'Apply')}
        </button>
        <button type="button" onClick={() => void handleReset()} disabled={busy}
          title={t('common.field_editor.reset_tooltip', "Remove this screen's personalization at the current tier")}
          className="rounded border border-csm-border bg-white px-3 py-0.5 text-csm-xs text-csm-danger hover:border-csm-danger disabled:opacity-60">
          {t('common.field_editor.reset', 'Reset')}
        </button>
        <button type="button" onClick={resetToLayout} disabled={busy}
          className="rounded border border-csm-border bg-white px-3 py-0.5 text-csm-xs text-csm-ink-soft hover:border-primary disabled:opacity-60">
          {t('common.cancel', 'Cancel')}
        </button>
      </div>

      {error && <div className="mb-2 text-csm-xs text-csm-danger">{error}</div>}

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={editorOrder} strategy={verticalListSortingStrategy}>
          {editorOrder.map(key => {
            const def = fieldMap.get(key);
            if (!def) return null;
            return (
              <SortableFieldCard key={key} id={key} label={def.label} type={def.type}
                hidden={hiddenKeys.has(key)} required={requiredKeys.has(key)} readonly={readonlyKeys.has(key)}
                onToggleHide={() => toggleHide(key)} onToggleRequired={() => toggleRequired(key)} onToggleReadonly={() => toggleReadonly(key)} />
            );
          })}
        </SortableContext>
      </DndContext>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SectionedDesignEditorPanel — multi-section editor (drag within / across
// sections + reorder whole sections)
// ---------------------------------------------------------------------------

const SECTION_PREFIX = 'section:';

function SortableSection({ id, label, children }: { id: string; label?: string; children: React.ReactNode }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className="mb-3 rounded-md border border-csm-border bg-csm-surface-alt p-2">
      <div className="mb-2 flex items-center gap-2">
        <span {...attributes} {...listeners}
          className="cursor-grab select-none text-csm-muted hover:text-csm-ink active:cursor-grabbing"
          title={t('common.field_editor.drag_reorder_section', 'Drag to reorder section')}>
          <GripVertical size={14} />
        </span>
        <span className="text-csm-sm font-semibold text-csm-ink">{label ?? '—'}</span>
      </div>
      {children}
    </div>
  );
}

interface SectionedDesignEditorPanelProps {
  fields: FieldDef[];
  sections: SectionDef[];
  pageCode: string;
  onApplied?: () => void;
}

function SectionedDesignEditorPanel({ fields, sections, pageCode, onApplied }: SectionedDesignEditorPanelProps) {
  const { t } = useTranslation();
  const ws = asfWorkspaceCode() || '';
  const { editTier, roleOwner, componentDelta, resetComponent } = usePersonalization();
  // Editing baseline = tiers below the edit tier + this tier's own delta.
  const baseSections = useMemo(
    () => applySectionedLayout(toUldCoreLayout(fields), sections, componentDelta('')).sections,
    [fields, sections, componentDelta],
  );
  const fieldMap = useMemo(() => {
    const m = new Map<string, FieldDef>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  // Default (core) section for a field, constrained to the known section set.
  const coreSectionKey = useCallback((f: FieldDef | undefined): string => {
    const first = sections[0]?.key ?? '';
    if (!f) return first;
    return (f.section && sections.some(s => s.key === f.section)) ? f.section : first;
  }, [sections]);

  const buildInitial = useCallback(() => {
    const bySection: Record<string, string[]> = {};
    for (const s of sections) bySection[s.key] = [];
    const behavior = { hidden: new Set<string>(), required: new Set<string>(), readonly: new Set<string>() };
    if (baseSections.length > 0) {
      for (const sec of baseSections) {
        bySection[sec.key] = bySection[sec.key] ?? [];
        for (const it of sec.items) {
          bySection[sec.key].push(it.key);
          if (it.visible === false) behavior.hidden.add(it.key);
          if (it.required === true) behavior.required.add(it.key);
          if (it.readonly === true) behavior.readonly.add(it.key);
        }
      }
      return { order: baseSections.map(s => s.key), bySection, behavior };
    }
    // Fallback: group by FieldDef.section in sectionDefs order.
    for (const f of fields) bySection[coreSectionKey(f)].push(f.key);
    return { order: sections.map(s => s.key), bySection, behavior };
  }, [baseSections, sections, fields, coreSectionKey]);

  const initial = useMemo(buildInitial, [buildInitial]);
  const [sectionOrder, setSectionOrder] = useState<string[]>(initial.order);
  const [fieldsBySection, setFieldsBySection] = useState<Record<string, string[]>>(initial.bySection);
  const [hiddenKeys, setHiddenKeys]     = useState<Set<string>>(initial.behavior.hidden);
  const [requiredKeys, setRequiredKeys] = useState<Set<string>>(initial.behavior.required);
  const [readonlyKeys, setReadonlyKeys] = useState<Set<string>>(initial.behavior.readonly);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sectionLabel = (key: string) => sections.find(s => s.key === key)?.label;

  // Clear the shared dirty flag when the editor unmounts (design mode exits).
  useEffect(() => () => setDesignDirty(false), []);

  const resetToLayout = useCallback(() => {
    const init = buildInitial();
    setSectionOrder(init.order);
    setFieldsBySection(init.bySection);
    setHiddenKeys(init.behavior.hidden);
    setRequiredKeys(init.behavior.required);
    setReadonlyKeys(init.behavior.readonly);
    setError(null);
    setDesignDirty(false);
  }, [buildInitial]);

  // Reset = delete this tier's saved personalization for the form; the provider
  // bumps resetTick so the (keyed) panel re-seeds from what remains below.
  const handleReset = useCallback(async () => {
    setBusy(true); setError(null);
    try { await resetComponent(''); setDesignDirty(false); }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }, [resetComponent]);

  const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) => (key: string) => {
    setDesignDirty(true);
    setter(prev => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });
  };
  const toggleHide     = toggleIn(setHiddenKeys);
  const toggleRequired = toggleIn(setRequiredKeys);
  const toggleReadonly = toggleIn(setReadonlyKeys);

  // Move a field between sections live, the standard dnd-kit multi-container pattern.
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId.startsWith(SECTION_PREFIX)) return; // a section drag, not a field
    setFieldsBySection(prev => {
      const activeSection = Object.keys(prev).find(sec => prev[sec].includes(activeId));
      const overSection = overId.startsWith(SECTION_PREFIX)
        ? overId.slice(SECTION_PREFIX.length)
        : Object.keys(prev).find(sec => prev[sec].includes(overId));
      if (!activeSection || !overSection || activeSection === overSection) return prev;
      const next = { ...prev, [activeSection]: prev[activeSection].filter(k => k !== activeId) };
      const overItems = [...prev[overSection]];
      const overIdx = overId.startsWith(SECTION_PREFIX) ? overItems.length : Math.max(0, overItems.indexOf(overId));
      overItems.splice(overIdx, 0, activeId);
      next[overSection] = overItems;
      return next;
    });
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId !== overId) setDesignDirty(true);

    if (activeId.startsWith(SECTION_PREFIX)) {
      if (overId.startsWith(SECTION_PREFIX) && activeId !== overId) {
        setSectionOrder(prev => {
          const a = prev.indexOf(activeId.slice(SECTION_PREFIX.length));
          const b = prev.indexOf(overId.slice(SECTION_PREFIX.length));
          return a === -1 || b === -1 ? prev : arrayMove(prev, a, b);
        });
      }
      return;
    }
    // Field reorder within its (possibly newly-assigned) section.
    if (overId.startsWith(SECTION_PREFIX)) return; // cross-section already handled in onDragOver
    setFieldsBySection(prev => {
      const sec = Object.keys(prev).find(s => prev[s].includes(activeId));
      const overSec = Object.keys(prev).find(s => prev[s].includes(overId));
      if (!sec || sec !== overSec) return prev;
      const oldIdx = prev[sec].indexOf(activeId);
      const newIdx = prev[sec].indexOf(overId);
      if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return prev;
      return { ...prev, [sec]: arrayMove(prev[sec], oldIdx, newIdx) };
    });
  }, []);

  const handleApply = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const deltas: UldDeltaAction[] = [];
      // Section order.
      sectionOrder.forEach((secKey, i) => {
        if (sections.findIndex(s => s.key === secKey) !== i) {
          deltas.push({ action: 'reorder_section', component_key: secKey, index: i });
        }
      });
      // Original absolute order (sectionDefs order, then declaration order within section).
      const originalFlat = sections.flatMap(s => fields.filter(f => coreSectionKey(f) === s.key).map(f => f.key));
      let absIdx = 0;
      for (const secKey of sectionOrder) {
        for (const key of (fieldsBySection[secKey] ?? [])) {
          const def = fieldMap.get(key);
          if (secKey !== coreSectionKey(def)) deltas.push({ action: 'set_section', component_key: key, section: secKey });
          if (originalFlat.indexOf(key) !== absIdx) deltas.push({ action: 'reorder_index', component_key: key, index: absIdx });
          absIdx++;
        }
      }
      deltas.push(...behaviorDeltas(
        fields.map(f => f.key), initial.behavior,
        { hidden: hiddenKeys, required: requiredKeys, readonly: readonlyKeys },
      ));

      const { error: e } = await saveUldDesign(pageCode, '', editTier ?? 'SYSTEM', ws, deltas);
      if (e) throw new Error(tierErrorMessage(e, roleOwner));
      setDesignDirty(false);
      onApplied?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }, [sectionOrder, fieldsBySection, hiddenKeys, requiredKeys, readonlyKeys, initial, fields, sections, fieldMap, coreSectionKey, pageCode, ws, editTier, roleOwner, onApplied]);

  return (
    <div className="mb-4 rounded-md border border-primary bg-white p-3">
      <div className="mb-3 flex items-center gap-2">
        <span className="flex-1 text-csm-sm font-semibold text-csm-ink">{t('common.field_editor.title', 'Field Layout Editor')}</span>
        <button type="button" onClick={() => void handleApply()} disabled={busy}
          className="rounded border border-primary bg-primary px-3 py-0.5 text-csm-xs text-white hover:opacity-90 disabled:opacity-60">
          {busy ? t('common.field_editor.saving', 'Saving...') : t('common.field_editor.apply', 'Apply')}
        </button>
        <button type="button" onClick={() => void handleReset()} disabled={busy}
          title={t('common.field_editor.reset_tooltip', "Remove this screen's personalization at the current tier")}
          className="rounded border border-csm-border bg-white px-3 py-0.5 text-csm-xs text-csm-danger hover:border-csm-danger disabled:opacity-60">
          {t('common.field_editor.reset', 'Reset')}
        </button>
        <button type="button" onClick={resetToLayout} disabled={busy}
          className="rounded border border-csm-border bg-white px-3 py-0.5 text-csm-xs text-csm-ink-soft hover:border-primary disabled:opacity-60">
          {t('common.cancel', 'Cancel')}
        </button>
      </div>

      {error && <div className="mb-2 text-csm-xs text-csm-danger">{error}</div>}

      <DndContext collisionDetection={closestCorners} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <SortableContext items={sectionOrder.map(k => `${SECTION_PREFIX}${k}`)} strategy={verticalListSortingStrategy}>
          {sectionOrder.map(secKey => (
            <SortableSection key={secKey} id={`${SECTION_PREFIX}${secKey}`} label={sectionLabel(secKey)}>
              <SortableContext items={fieldsBySection[secKey] ?? []} strategy={verticalListSortingStrategy}>
                {(fieldsBySection[secKey] ?? []).map(key => {
                  const def = fieldMap.get(key);
                  if (!def) return null;
                  return (
                    <SortableFieldCard key={key} id={key} label={def.label} type={def.type}
                      hidden={hiddenKeys.has(key)} required={requiredKeys.has(key)} readonly={readonlyKeys.has(key)}
                      onToggleHide={() => toggleHide(key)} onToggleRequired={() => toggleRequired(key)} onToggleReadonly={() => toggleReadonly(key)} />
                  );
                })}
                {(fieldsBySection[secKey] ?? []).length === 0 && (
                  <div className="rounded border border-dashed border-csm-border px-2 py-3 text-center text-csm-2xs text-csm-muted">
                    {t('common.field_editor.drag_field_here', 'Drag a field here')}
                  </div>
                )}
              </SortableContext>
            </SortableSection>
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FieldRenderer — main export
// ---------------------------------------------------------------------------

interface FieldRendererProps {
  fields: FieldDef[];
  /** Flat-mode merged layout (single-section pages). */
  layout?: UldLayoutItem[];
  /** Section defs — presence flips FieldRenderer into section mode. */
  sections?: SectionDef[];
  /** Section-mode merged layout (from useUldSectionedLayout). */
  sectionLayout?: UldSection[];
  columns?: number;
  render: (f: FieldLayoutItem) => React.ReactNode;
  designMode?: boolean;
  pageCode?: string;
  session?: AuthSession;
  /** Called after a successful save so the parent can exit design mode. */
  onDesignApplied?: () => void;
}

export function FieldRenderer({
  fields, layout = [], sections, sectionLayout = [], columns = 2, render,
  designMode, pageCode, onDesignApplied,
}: FieldRendererProps) {
  const ctx = usePersonalizationOptional();
  const fieldMap = useMemo(() => {
    const m = new Map<string, FieldDef>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  const gridCols = GRID_COLS_MAP[columns] ?? GRID_COLS_MAP[2];
  // The single personalization button drives the design panel; `designMode` is a
  // legacy override for any caller still passing it explicitly.
  const showDesignEditor = (ctx?.isPersonalizing === true || designMode === true) && !!pageCode;

  // Register this panel as the active editor for its component ('') so
  // PersonalizedField defers to it (renders read-only, no duplicate inline editor).
  const register = ctx?.registerDesignPanel;
  useEffect(() => {
    if (!register) return;
    if (showDesignEditor) { register(''); return () => register(null); }
  }, [register, showDesignEditor]);

  const renderItem = useCallback((item: UldLayoutItem) => {
    const def = fieldMap.get(item.key);
    if (!def) return null;
    const span = typeof item.col_span === 'number' ? item.col_span : (def.col_span ?? 1);
    const spanClass = span >= columns ? 'col-span-full' : (COL_SPAN_MAP[span] ?? '');
    const merged: FieldLayoutItem = { ...def, label: item.label ?? def.label, col_span: span };
    return (
      <div key={item.key} data-field-key={item.key} className={spanClass}>
        {render(merged)}
      </div>
    );
  }, [fieldMap, columns, render]);

  // ── Section mode ──────────────────────────────────────────────────────────
  if (sections && sections.length > 0) {
    // Fall back to definition grouping while the delta loads.
    const groups: UldSection[] = sectionLayout.length > 0
      ? sectionLayout
      : sections.map(s => ({
          key: s.key, label: s.label, order: 0,
          items: fields.filter(f => (f.section && sections.some(x => x.key === f.section) ? f.section : sections[0].key) === s.key)
                       .map(f => ({ key: f.key, label: f.label, col_span: f.col_span ?? 1, visible: true })),
        }));

    return (
      <>
        {showDesignEditor && pageCode && (
          <SectionedDesignEditorPanel
            key={`${ctx?.editTier}:${ctx?.resetTick}`}
            fields={fields}
            sections={sections}
            pageCode={pageCode}
            onApplied={onDesignApplied}
          />
        )}

        {groups.map(sec => {
          const visible = sec.items.filter(it => it.visible !== false);
          if (visible.length === 0) return null;
          return (
            <div key={sec.key} className="mb-4 rounded-md border border-csm-border bg-csm-surface p-3">
              {sec.label && (
                <h3 className="mb-2 text-csm-sm font-semibold text-csm-ink-soft">{sec.label}</h3>
              )}
              <div className={`${gridCols} grid gap-3`}>
                {visible.map(renderItem)}
              </div>
            </div>
          );
        })}
      </>
    );
  }

  // ── Flat mode (existing behaviour) ────────────────────────────────────────
  const effectiveLayout: UldLayoutItem[] = layout.length > 0 ? layout : toUldCoreLayout(fields);

  return (
    <>
      {showDesignEditor && pageCode && (
        <DesignEditorPanel
          key={`${ctx?.editTier}:${ctx?.resetTick}`}
          fields={fields}
          pageCode={pageCode}
          onApplied={onDesignApplied}
        />
      )}

      <div className={`${gridCols} grid gap-3`}>
        {effectiveLayout.filter(item => item.visible !== false).map(renderItem)}
      </div>
    </>
  );
}
